import type {
  PatientApi,
  PatientApiUpdateQuery,
  TranscriptionApi,
  InfoApi,
  PatientSearchApiQuery,
  PatientPlanApiQuery,
  PatientStatusApiQuery,
  PlanToPatientApiQuery,
} from "@/Modules/Patients/Models/Api";
import type { SignDocumentApi } from "@/Modules/SignDocuments/Models/Api";

import { get2, patch2, post2, remove2 } from "./utils/FunctionalTherapyApi";

export async function getPatients(): Promise<PatientApi[]> {
  return get2<void, PatientApi[]>(`/v1.0/clients`);
}

export async function getPlans(): Promise<InfoApi[]> {
  return get2<void, InfoApi[]>(`/v1.0/clients/plans`);
}

export async function getStates(): Promise<InfoApi[]> {
  return get2<void, InfoApi[]>(`/v1.0/clients/statuses`);
}

export function getPatientById(uuid: string): Promise<PatientApi> {
  return get2<void, PatientApi>(`/v1.0/clients/${uuid}`);
}

export function getPatientsBySearch(search: string): Promise<PatientApi[]> {
  return get2<PatientSearchApiQuery, PatientApi[]>("/v1.0/clients", {
    search: search,
  });
}

export function getPatientsByPlan(planUuid: string): Promise<PatientApi[]> {
  return get2<PatientPlanApiQuery, PatientApi[]>("/v1.0/clients", {
    plan: planUuid,
  });
}

export function getPatientsByStatus(status: string): Promise<PatientApi[]> {
  return get2<PatientStatusApiQuery, PatientApi[]>("/v1.0/clients", {
    status: status,
  });
}

export async function updatePatient(uuid: string, patientData: PatientApiUpdateQuery): Promise<PatientApi> {
  return patch2<PatientApiUpdateQuery, PatientApi>(`/v1.0/clients/${uuid}`, patientData);
}

export function deletePatient(uuid: string): Promise<void> {
  return remove2<void, void>(`/v1.0/clients/${uuid}`);
}

export function requestTranscriptionEnablement(uuid: string): Promise<TranscriptionApi> {
  return post2<void, TranscriptionApi>(`/v1.0/clients/${uuid}/transcription`);
}

export function requestTranscriptionDisablement(uuid: string): Promise<TranscriptionApi> {
  return remove2<void, TranscriptionApi>(`/v1.0/clients/${uuid}/transcription`);
}

export function sendMissingEmail(uuid: string) {
  return post2<void, boolean>(`/v1.0/clients/${uuid}/missing-user`);
}

export function discharge(uuid: string, reason: string): Promise<unknown> {
  return post2<{ discharge_reason: string }, unknown>(`/v1.0/clients/${uuid}/discharge`, {
    discharge_reason: reason,
  });
}

export function sendPlanToPatient(uuid: string, planInfo: PlanToPatientApiQuery): Promise<void> {
  return post2<PlanToPatientApiQuery, void>(`/v1.0/clients/${uuid}/private_plans`, planInfo);
}

export const getSignDocuments = async (uuid: string): Promise<SignDocumentApi[]> => {
  return get2<void, SignDocumentApi[]>(`/v1.0/clients/${uuid}/documents`);
};

export function sendDocumentToSign(uuid: string, documentUuid: string): Promise<SignDocumentApi> {
  return post2<void, SignDocumentApi>(`/v1.0/clients/${uuid}/documents/${documentUuid}`);
}
